import { expected } from '@aloft/toolkit-shared';
import { Row, Table, getSortedRowModel } from '@tanstack/react-table';

import { isPinned } from '../../../ToolkitPropertyTable/isPinned.ts';

function putPinnedFirst(rows: Row<unknown>[], rowsToPin: number) {
  return [
    ...rows.filter((r) => isPinned(rowsToPin, r.index)).sort((a, b) => a.index - b.index),
    ...rows.filter((r) => !isPinned(rowsToPin, r.index)),
  ];
}

function partitionUnpinnedRows(
  rows: Row<unknown>[],
  rowsToPin: number,
  getGroupId: (row: unknown) => string | undefined,
  groupOrder: string[],
): Row<unknown>[] {
  const pinned = rows
    .filter((r) => isPinned(rowsToPin, r.index))
    .sort((a, b) => a.index - b.index);
  const unpinned = rows.filter((r) => !isPinned(rowsToPin, r.index));
  const buckets = new Map<string, Row<unknown>[]>();
  const other: Row<unknown>[] = [];

  groupOrder.forEach((id) => buckets.set(id, []));

  unpinned.forEach((row) => {
    const groupId = getGroupId(row.original);
    if (groupId && buckets.has(groupId)) {
      buckets.get(groupId)!.push(row);
    } else {
      other.push(row);
    }
  });

  return [...pinned, ...groupOrder.flatMap((id) => buckets.get(id) ?? []), ...other];
}

export function getSortedRowModelWithPinnedSubject(rowsToPin: number) {
  const custom = (table: Table<unknown>) => {
    const rowModel = getSortedRowModel()(table);

    const customRows = () => {
      const { rows, flatRows, rowsById } = rowModel();
      return {
        rows: expected(putPinnedFirst(rows, rowsToPin)),
        flatRows: expected(putPinnedFirst(flatRows, rowsToPin)),
        rowsById: expected(rowsById),
      };
    };

    return customRows;
  };

  return custom;
}

export function getSortedRowModelWithPinnedSubjectAndGroups<T>(
  rowsToPin: number,
  getGroupId: (row: T) => string | undefined,
  groupOrder: string[],
) {
  const custom = (table: Table<unknown>) => {
    const rowModel = getSortedRowModel()(table);

    const customRows = () => {
      const { rows, flatRows, rowsById } = rowModel();
      const partition = (modelRows: Row<unknown>[]) =>
        partitionUnpinnedRows(
          modelRows,
          rowsToPin,
          (row) => getGroupId(row as T),
          groupOrder,
        );
      return {
        rows: expected(partition(rows)),
        flatRows: expected(partition(flatRows)),
        rowsById: expected(rowsById),
      };
    };

    return customRows;
  };

  return custom;
}
